import { useEffect, useMemo, useRef, useState } from "react";
import {
  assembleLpSmbHomeContent, splitLpSmbHomeContent, LP_SMB_HOME_BLOCK_KEYS,
  LP_SMB_HOME_ANCHOR_BLOCKS, smbBlockHasContent, smbCtaDestination,
  type LpSmbHomeNavAnchor, smbEditableBindingPath,
} from "@shared/lp-express-smb-home";
import {
  LP_PRODUCT_HERO_BLOCK_KEYS,
  LP_PRODUCT_HERO_IMAGE_SLOTS,
  type LpProductHeroProductDetails,
} from "@shared/lp-express-product-hero";
import {
  isLpStudioDesignSnapshot,
  parseLpStudioBindingPath as parseProductHeroBindingPath,
  type LpStudioImageBinding,
} from "@shared/lp-express-studio";
import { lpGetAt } from "@shared/lp-deep-set";
import {
  LP_THEME_FONTS,
  LP_SECTION_REGISTRY,
  LP_SECTION_SCROLL_MARGIN,
  lpSectionAnchorId,
  sanitizeLpTheme,
  type LpElementStyle,
  type LpSection,
  type LpTheme,
} from "@shared/lp-express-sections";
import { autoCtaTextColor } from "@shared/brand-cta-style";
import {
  LpImageActions,
  LpReviewActions,
  LpSectionRegenerate,
  LpStylePopover,
  type LpEditorControls,
  type LpReviewChoice,
} from "./lp-editor";

function parseLpStudioBindingPath(raw: unknown): Array<string | number> | null {
  return parseProductHeroBindingPath(raw) ??
    (typeof raw === "string" ? smbEditableBindingPath(raw) : null);
}

export type StudioPresentation = {
  cssVariables?: Record<string, string>;
  rootClassName?: string;
  fontCss?: string;
  bridgeCss?: string;
};

type StudioTemplateProps = {
  generatedContent: Record<string, any>;
  productPhotos: string[];
  logoUrl?: string | null;
  brandName?: string | null;
  offerText?: string | null;
  groundedProductDetails?: LpProductHeroProductDetails | null;
  ctaUrl?: string | null;
  editor?: LpEditorControls;
  presentation?: StudioPresentation;
  sections?: LpSection[];
  onRetryImage?: (slotKey: string) => void;
  reviews?: LpReviewChoice[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function persistedString(
  object: Record<string, unknown>,
  key: string,
  fallback: string,
): string {
  if (!Object.prototype.hasOwnProperty.call(object, key)) return fallback;
  // A persisted explicit empty string is meaningful: the editor uses it to
  // clear a leaf. Malformed non-string leaves are treated as explicitly blank,
  // never replaced with a default that would erase their presence semantics.
  return typeof object[key] === "string" ? object[key] : "";
}

export function mergeGroundedStudioProductDetails(
  grounded: LpProductHeroProductDetails,
  persisted: unknown,
): LpProductHeroProductDetails {
  if (!isRecord(persisted)) return grounded;
  const persistedSpecs = Array.isArray(persisted.specs)
    ? persisted.specs
    : [];
  const specCount = Math.max(grounded.specs.length, persistedSpecs.length);
  const specs = Array.from({ length: specCount }, (_, index) => {
    const groundedSpec = grounded.specs[index] ?? { label: "", value: "" };
    const persistedSpec = isRecord(persistedSpecs[index])
      ? persistedSpecs[index]
      : {};
    return {
      label: persistedString(persistedSpec, "label", groundedSpec.label),
      value: persistedString(persistedSpec, "value", groundedSpec.value),
    };
  });
  return {
    label: persistedString(persisted, "label", grounded.label),
    heading: persistedString(persisted, "heading", grounded.heading),
    description: persistedString(
      persisted,
      "description",
      grounded.description,
    ),
    specs,
  };
}

const LP_BUTTON_RADIUS: Record<string, string> = {
  rounded: "0.5rem",
  pill: "9999px",
  square: "0px",
};

function studioThemePresentation(themeInput: unknown): {
  cssVariables: Record<string, string>;
  rootClassName: string;
} {
  const theme = sanitizeLpTheme(themeInput) as LpTheme;
  const vars: Record<string, string> = {};
  if (theme.primaryColor) vars["--color-brand-navy"] = theme.primaryColor;
  if (theme.accentColor) vars["--color-brand-coral"] = theme.accentColor;
  if (theme.headingFont) vars["--lp-heading-font"] = LP_THEME_FONTS[theme.headingFont];
  if (theme.bodyFont) vars["--lp-body-font"] = LP_THEME_FONTS[theme.bodyFont];
  if (theme.buttonShape) vars["--lp-btn-radius"] = LP_BUTTON_RADIUS[theme.buttonShape];
  if (theme.ctaColor) {
    vars["--lp-cta-fill"] = theme.ctaColor;
    vars["--lp-cta-text"] =
      theme.ctaTextColor ||
      (theme.buttonFill === "outline"
        ? theme.ctaColor
        : autoCtaTextColor(theme.ctaColor));
  }
  const classes = ["lp-wysiwyg-root"];
  if (theme.spacing === "compact") classes.push("lp-space-compact");
  if (theme.spacing === "spacious") classes.push("lp-space-spacious");
  if (theme.buttonShape) classes.push("lp-btn-shaped");
  if (theme.buttonFill === "outline") classes.push("lp-btn-outline");
  if (theme.ctaColor) classes.push("lp-cta-branded");
  if (theme.ctaAllCaps) classes.push("lp-cta-caps");
  if (theme.ctaFontFamily) classes.push("lp-cta-font");
  return { cssVariables: vars, rootClassName: classes.join(" ") };
}

function imageUrlFor(
  binding: LpStudioImageBinding,
  generatedContent: Record<string, any>,
  productPhotos: string[],
  logoUrl?: string | null,
): { url: string; status: string } {
  const overrides =
    generatedContent.mediaOverrides &&
    typeof generatedContent.mediaOverrides === "object"
      ? generatedContent.mediaOverrides
      : {};
  if (binding === "logo") {
    const url = typeof logoUrl === "string" ? logoUrl.trim() : "";
    return { url, status: url ? "ready" : "missing" };
  }
  if (binding === "product") {
    const url =
      overrides["phBuyBox:gallery-main"]?.url ??
      productPhotos.find((candidate) => typeof candidate === "string" && candidate.trim()) ??
      "";
    return { url, status: url ? "ready" : "missing" };
  }
  const index = binding === "lifestyle-1" ? 0 : 1;
  const slotKey = LP_PRODUCT_HERO_IMAGE_SLOTS[index]?.key;
  const slot = Array.isArray(generatedContent.productHeroImages)
    ? generatedContent.productHeroImages.find(
        (candidate: any) => candidate?.key === slotKey,
      )
    : null;
  const overrideUrl = slotKey
    ? overrides[`phBuyBox:gallery-ai-${slotKey}`]?.url
    : "";
  const url = overrideUrl ?? slot?.imageUrl ?? "";
  return {
    url,
    status: url ? "ready" : slot?.imageStatus ?? "pending",
  };
}

const PRODUCT_HERO_SECTION_TYPES = new Set<string>(
  [...LP_PRODUCT_HERO_BLOCK_KEYS, ...LP_SMB_HOME_BLOCK_KEYS],
);

function studioSectionRoot(binding: string | null): string | null {
  const root = String(parseLpStudioBindingPath(binding)?.[0] ?? "");
  return PRODUCT_HERO_SECTION_TYPES.has(root) ? root : null;
}

function rootsWithin(element: Element): Set<string> {
  const roots = new Set<string>();
  const ownRoot = studioSectionRoot(element.getAttribute("data-lp-bind"));
  if (ownRoot) roots.add(ownRoot);
  element.querySelectorAll("[data-lp-bind]").forEach((bound) => {
    const root = studioSectionRoot(bound.getAttribute("data-lp-bind"));
    if (root) roots.add(root);
  });
  return roots;
}

function studioSectionContainers(document: Document): Map<string, HTMLElement[]> {
  const candidates = new Map<string, Set<HTMLElement>>();
  document.querySelectorAll<HTMLElement>("[data-lp-bind]").forEach((bound) => {
    const root = studioSectionRoot(bound.getAttribute("data-lp-bind"));
    if (!root) return;
    let candidate: HTMLElement = bound;
    for (
      let parent = bound.parentElement;
      parent && parent !== document.body;
      parent = parent.parentElement
    ) {
      const roots = rootsWithin(parent);
      if (roots.size !== 1 || !roots.has(root)) break;
      candidate = parent;
    }
    const current = candidates.get(root) ?? new Set<HTMLElement>();
    current.add(candidate);
    candidates.set(root, current);
  });

  const result = new Map<string, HTMLElement[]>();
  for (const [root, elements] of candidates) {
    const outermost = Array.from(elements).filter(
      (candidate) =>
        !Array.from(elements).some(
          (other) => other !== candidate && other.contains(candidate),
        ),
    );
    outermost.forEach((element) => element.setAttribute("data-lp-section", root));
    result.set(root, outermost);
  }
  return result;
}

type StudioComponentGroup = {
  key: string;
  sectionType: string;
  componentKeys: string[];
};

type StudioInlineTarget = {
  sectionType?: string;
  sectionTop?: number;
  binding?: string;
  componentKey?: string;
  imageBinding?: LpStudioImageBinding;
  targetTop?: number;
};

function annotateStudioComponents(
  containers: Map<string, HTMLElement[]>,
): StudioComponentGroup[] {
  const groups: StudioComponentGroup[] = [];
  for (const [sectionType, sectionContainers] of containers) {
    sectionContainers.forEach((container, containerIndex) => {
      const children = Array.from(container.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
      );
      if (children.length < 2) return;
      const key = `studio:${sectionType}:${containerIndex}`;
      const componentKeys = children.map((child, childIndex) => {
        const componentKey = `${key}:${childIndex}`;
        child.dataset.lpComponent = componentKey;
        return componentKey;
      });
      groups.push({ key, sectionType, componentKeys });
    });
  }
  return groups;
}

function reorderElements(
  elements: HTMLElement[],
  requestedOrder: string[] | undefined,
): void {
  if (elements.length < 2 || !requestedOrder?.length) return;
  const byKey = new Map(elements.map((element) => [element.dataset.lpComponent, element]));
  const ordered = requestedOrder
    .map((key) => byKey.get(key))
    .filter((element): element is HTMLElement => Boolean(element));
  for (const element of elements) {
    if (!ordered.includes(element)) ordered.push(element);
  }
  const placeholders = elements.map((element) => {
    const placeholder = element.ownerDocument.createComment("lp-component");
    element.replaceWith(placeholder);
    return placeholder;
  });
  placeholders.forEach((placeholder, index) => placeholder.replaceWith(ordered[index]));
}

function styleStudioElement(element: HTMLElement, style: LpElementStyle | undefined): void {
  if (!style) return;
  if (typeof style.fontSize === "number") element.style.fontSize = `${style.fontSize}px`;
  if (style.fontWeight) element.style.fontWeight = String(style.fontWeight);
  if (style.align) element.style.textAlign = style.align;
  if (style.color) element.style.color = style.color;
  if (style.fontFamily) element.style.fontFamily = style.fontFamily;
  if (style.background) element.style.backgroundColor = style.background;
  if (style.paddingY === "compact") {
    element.style.paddingTop = "1.5rem";
    element.style.paddingBottom = "1.5rem";
  } else if (style.paddingY === "spacious") {
    element.style.paddingTop = "5rem";
    element.style.paddingBottom = "5rem";
  }
}

function reviewValueForPath(
  generatedContent: Record<string, any>,
  path: Array<string | number>,
): string | number | undefined {
  if (
    path[0] !== "phReviews" ||
    path[1] !== "items" ||
    typeof path[2] !== "number"
  ) {
    return undefined;
  }
  const override = generatedContent.reviewOverrides?.[`studio-review-${path[2]}`];
  if (!override || typeof override !== "object") return undefined;
  if (path[3] === "name") return override.reviewer ?? "";
  if (path[3] === "text") return override.reviewText;
  if (path[3] === "rating") return override.rating;
  return undefined;
}

function studioItemIdentity(binding: string | null): {
  key: string;
  path: Array<string | number>;
  index: number;
} | null {
  const path = parseLpStudioBindingPath(binding);
  if (!path) return null;
  const indexPosition = path.findIndex((part) => typeof part === "number");
  if (indexPosition < 0) return null;
  const index = path[indexPosition] as number;
  const arrayPath = path.slice(0, indexPosition);
  return { key: `${arrayPath.join(".")}:${index}`, path: arrayPath, index };
}

function applyStudioElementPresentation(
  document: Document,
  generatedContent: Record<string, any>,
  editable: boolean,
): void {
  const states = generatedContent.elements ?? {};
  const styles = generatedContent.styles ?? {};
  const itemElements = new Map<string, { identity: ReturnType<typeof studioItemIdentity>; elements: HTMLElement[] }>();
  document.querySelectorAll<HTMLElement>("[data-lp-bind]").forEach((element) => {
    const binding = element.getAttribute("data-lp-bind")?.trim() ?? "";
    const path = parseLpStudioBindingPath(binding);
    if (!path) return;
    const elementKey = `f:${binding}`;
    const state = states[elementKey];
    if (state?.deleted) {
      element.remove();
      return;
    }
    if (state?.hidden) {
      if (editable) element.dataset.lpElementHidden = "true";
      else element.hidden = true;
    }
    const identity = studioItemIdentity(binding);
    if (identity) {
      const entry = itemElements.get(identity.key) ?? { identity, elements: [] };
      entry.elements.push(element);
      itemElements.set(identity.key, entry);
    }
    styleStudioElement(element, styles[elementKey]);
  });
  for (const { identity, elements } of itemElements.values()) {
    if (!identity) continue;
    const items = lpGetAt(generatedContent, identity.path);
    const item = Array.isArray(items) ? items[identity.index] : undefined;
    let container = elements[0];
    for (
      let parent = container?.parentElement;
      parent && parent !== document.body;
      parent = parent.parentElement
    ) {
      const identities = new Set(
        Array.from(parent.querySelectorAll("[data-lp-bind]"))
          .map((bound) => studioItemIdentity(bound.getAttribute("data-lp-bind"))?.key)
          .filter(Boolean),
      );
      if (identities.size !== 1 || !identities.has(identity.key)) break;
      container = parent;
    }
    if (!container) continue;
    if (item === undefined) {
      container.hidden = true;
    } else if (item && typeof item === "object" && (item as any).hidden) {
      if (editable) container.dataset.lpItemHidden = "true";
      else container.hidden = true;
    }
  }
}

function applyStudioSectionLayout(
  document: Document,
  generatedContent: Record<string, any>,
  sections: LpSection[] | undefined,
  editable: boolean,
): void {
  const containers = studioSectionContainers(document);
  if (!sections) return;
  const layout = new Map(sections.map((section, index) => [section.type, { section, index }]));

  for (const [type, elements] of containers) {
    const entry = layout.get(type as LpSection["type"]);
    for (const element of elements) {
      element.id ||= lpSectionAnchorId(type as LpSection["type"]);
      element.style.scrollMarginTop = `${LP_SECTION_SCROLL_MARGIN}px`;
      styleStudioElement(
        element,
        generatedContent.styles?.[`sec:${type}`],
      );
      if (!entry) {
        element.hidden = true;
      } else if (entry.section.hidden) {
        if (editable) element.setAttribute("data-lp-section-hidden", "true");
        else element.hidden = true;
      }
    }
  }

  const byParent = new Map<HTMLElement, HTMLElement[]>();
  for (const elements of containers.values()) {
    for (const element of elements) {
      const parent = element.parentElement;
      if (!parent) continue;
      const siblings = byParent.get(parent) ?? [];
      siblings.push(element);
      byParent.set(parent, siblings);
    }
  }
  for (const elements of byParent.values()) {
    if (elements.length < 2) continue;
    const ordered = [...elements].sort((a, b) => {
      const aIndex = layout.get(a.dataset.lpSection as LpSection["type"])?.index ?? Number.MAX_SAFE_INTEGER;
      const bIndex = layout.get(b.dataset.lpSection as LpSection["type"])?.index ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    });
    const placeholders = elements.map((element) => {
      const placeholder = document.createComment("lp-section");
      element.replaceWith(placeholder);
      return placeholder;
    });
    placeholders.forEach((placeholder, index) => placeholder.replaceWith(ordered[index]));
  }
}

function buildSmbStudioSrcDoc(
  gc: Record<string, any>, editable: boolean, presentation?: StudioPresentation, sections?: LpSection[], regeneratingSections: readonly string[] = [],
): string {
  const document = new DOMParser().parseFromString(gc.studioDesign.html, "text/html");
  const copy = assembleLpSmbHomeContent(gc);
  const blocks = splitLpSmbHomeContent(copy);
  const runtime = { ...gc, ...blocks };
  const style = document.createElement("style");
  style.textContent = `[hidden]{display:none!important}html,body{margin:0;min-height:100%}
    [data-lp-section]{scroll-margin-top:${LP_SECTION_SCROLL_MARGIN}px}
    [data-lp-regenerating="true"]{opacity:.5;pointer-events:none}
    ${presentation?.fontCss || ""}${presentation?.bridgeCss || ""}`;
  document.head.append(style);
  const base = document.createElement("base");
  base.target = "_self";
  document.head.prepend(base);
  const theme = studioThemePresentation(gc.theme);
  document.body.className += ` ${theme.rootClassName} ${presentation?.rootClassName || ""}`;
  for (const [key, value] of Object.entries({ ...presentation?.cssVariables, ...theme.cssVariables }))
    if (key.startsWith("--") && value) document.body.style.setProperty(key, value);
  document.querySelectorAll<HTMLElement>("[data-lp-bind]").forEach(element => {
    const path = parseLpStudioBindingPath(element.dataset.lpBind);
    const value = path ? lpGetAt(runtime, path) : "";
    element.textContent = typeof value === "string" ? value : "";
    if (editable && path) {
      element.setAttribute("contenteditable", "true");
      element.dataset.lpStudioEditable = "true";
    }
    if (!element.textContent.trim()) {
      element.hidden = true;
      // Hide the complete repeated item, not only its empty copy leaves.
      const binding = element.dataset.lpBind || "";
      const prefix = binding.match(/^(.+\.(?:items|highlights|areas)\.\d+)(?:\.|$)/)?.[1];
      if (prefix) {
        let row = element;
        while (row.parentElement && !/^(SECTION|MAIN|BODY|NAV)$/.test(row.parentElement.tagName)) {
          const siblings = Array.from(row.parentElement.querySelectorAll<HTMLElement>("[data-lp-bind]"));
          if (!siblings.every(bound => bound.dataset.lpBind?.startsWith(prefix + ".") || bound.dataset.lpBind === prefix)) break;
          row = row.parentElement;
        }
        if (!row.textContent?.trim()) row.hidden = true;
      }
    }
  });
  const assets = gc.smbHomeAssets ?? gc.studioCreation?.intent?.imageAssets ?? gc.studioBrandDesignInput?.imageAssets ?? {};
  document.querySelectorAll<HTMLElement>("[data-lp-image]").forEach(element => {
    const binding = element.dataset.lpImage || "";
    const url = /^(logo|work-photo-(?:[1-9]|1[0-2]))$/.test(binding) ? assets[binding] : null;
    element.removeAttribute("srcset");
    if (element.tagName === "IMG" && typeof url === "string" && /^https?:\/\//i.test(url)) {
      element.setAttribute("src", url);
      element.hidden = false;
      if (/^\[.*\]$/.test(element.getAttribute("alt") || "")) element.setAttribute("alt", "");
    } else {
      element.removeAttribute("src");
      element.hidden = true;
    }
  });
  applyStudioElementPresentation(document, runtime, editable);
  applyStudioSectionLayout(document, runtime, sections, editable);
  const containers = studioSectionContainers(document);
  for (const key of LP_SMB_HOME_BLOCK_KEYS) {
    for (const element of containers.get(key) ?? []) {
      element.id = lpSectionAnchorId(key);
      if (!smbBlockHasContent(key, blocks[key])) element.hidden = true;
      if (gc.lp2Regeneration?.copy?.status === "pending" || regeneratingSections.includes(key)) element.dataset.lpRegenerating = "true";
    }
  }
  const available = (anchor: string) => {
    const key = LP_SMB_HOME_ANCHOR_BLOCKS[anchor as LpSmbHomeNavAnchor];
    return key && (containers.get(key) ?? []).some(element => !element.hidden && !element.dataset.lpSectionHidden);
  };
  if (gc.smbActionSnapshot?.action?.source === "contact_section") {
    // Even a compatible minimal template or a hidden editor section must have a
    // visible destination. Never resurrect hidden content or template contacts.
    let target = document.getElementById("lp-sh-contact");
    if (!target || target.hidden || target.dataset.lpSectionHidden ||
        target.closest("[hidden], [data-lp-section-hidden]")) {
      if (target) target.removeAttribute("id");
      target = document.createElement("section");
      target.id = "lp-sh-contact";
      const heading = document.createElement("h2");
      heading.textContent = copy.contact.heading || "Contact";
      target.append(heading);
      const details = document.createElement("p");
      details.textContent = copy.contact.dek;
      target.append(details);
      (document.querySelector("main") || document.body).append(target);
    }
  }
  document.querySelectorAll<HTMLAnchorElement>("a").forEach(anchor => {
    if (anchor.hasAttribute("data-lp-cta")) {
      const contact = anchor.dataset.lpBind?.startsWith("contact.") || anchor.closest('[data-lp-section="shContact"]');
      anchor.textContent = contact ? copy.contact.ctaText : copy.primaryCtaText;
      const destination = smbCtaDestination(gc.ctaUrl);
      if (destination) { anchor.href = destination; anchor.target = destination.startsWith("#") ? "_self" : "_top"; anchor.rel = "noopener"; }
      else anchor.removeAttribute("href");
      return;
    }
    const index = anchor.getAttribute("data-lp-bind")?.match(/^nav\.items\.(\d+)\./)?.[1]
      ?? anchor.querySelector("[data-lp-bind]")?.getAttribute("data-lp-bind")?.match(/^nav\.items\.(\d+)\./)?.[1];
    const target = index !== undefined ? copy.nav.items[Number(index)]?.anchor : anchor.getAttribute("href")?.slice(1);
    if (!target || !available(target) || !copy.nav.items.some(item => item.anchor === target)) {
      anchor.hidden = true; anchor.removeAttribute("href"); return;
    }
    anchor.href = `#${lpSectionAnchorId(LP_SMB_HOME_ANCHOR_BLOCKS[target as LpSmbHomeNavAnchor])}`;
    anchor.target = "_self";
  });
  return `<!doctype html>${document.documentElement.outerHTML}`;
}

export function buildStudioSrcDoc(
  generatedContent: Record<string, any>,
  productPhotos: string[],
  ctaUrl: string | null | undefined,
  editable: boolean,
  presentation?: StudioPresentation,
  sections?: LpSection[],
  logoUrl?: string | null,
  brandName?: string | null,
  offerText?: string | null,
  groundedProductDetails?: LpProductHeroProductDetails | null,
  regeneratingSections: string[] = [],
): string {
  const design = generatedContent.studioDesign;
  if (!isLpStudioDesignSnapshot(design) || typeof DOMParser === "undefined") {
    return "";
  }
  if (design.rendererTemplateKey === "smb-home")
    return buildSmbStudioSrcDoc(generatedContent, editable, presentation, sections, regeneratingSections);
  const document = new DOMParser().parseFromString(design.html, "text/html");
  const existingProductDetails =
    generatedContent.phBuyBox?.productDetails;
  const mergedProductDetails = groundedProductDetails
    ? mergeGroundedStudioProductDetails(
        groundedProductDetails,
        existingProductDetails,
      )
    : existingProductDetails;
  // Persisted/generated editor leaves win individually. Missing siblings keep
  // their grounded values when lpDeepSet creates only the edited path.
  const runtimeContent =
    mergedProductDetails
      ? {
          ...generatedContent,
          phBuyBox: {
            ...generatedContent.phBuyBox,
            productDetails: mergedProductDetails,
          },
        }
      : generatedContent;
  const head = document.head || document.documentElement.prepend(document.createElement("head"));
  const base = document.createElement("base");
  base.target = "_self";
  head.prepend(base);

  const body = document.body;
  const themePresentation = studioThemePresentation(generatedContent.theme);
  const rootClasses = [
    themePresentation.rootClassName,
    presentation?.rootClassName ?? "",
  ]
    .flatMap((value) => value.split(/\s+/))
    .filter(Boolean);
  body.className = Array.from(new Set([...body.classList, ...rootClasses])).join(" ");
  for (const [name, value] of Object.entries({
    ...presentation?.cssVariables,
    ...themePresentation.cssVariables,
  })) {
    if (name.startsWith("--") && value) body.style.setProperty(name, value);
  }

  if (presentation?.fontCss) {
    const fontStyle = document.createElement("style");
    fontStyle.setAttribute("data-lp-studio-fonts", "true");
    fontStyle.textContent = presentation.fontCss;
    head.append(fontStyle);
  }

  const runtimeStyle = document.createElement("style");
  runtimeStyle.setAttribute("data-lp-studio-runtime", "true");
  runtimeStyle.textContent = `
    html,body{margin:0;min-height:100%}
    [hidden]{display:none!important}
    [data-lp-image-status="pending"]{background:#f3f4f6;min-height:120px}
    [data-lp-image-status="failed"]{background:#fef2f2;min-height:120px}
    [data-lp-image-status="missing"]{background:#f8fafc;min-height:120px}
    [data-lp-image-status]:not(img)[data-lp-generation-label="true"]{
      display:flex;align-items:center;justify-content:center;text-align:center;
      color:#64748b;font:600 13px/1.4 system-ui,sans-serif;padding:16px;box-sizing:border-box
    }
     [data-lp-section-hidden="true"]{opacity:.35;filter:grayscale(.35)}
     [data-lp-element-hidden="true"],[data-lp-item-hidden="true"]{opacity:.35;text-decoration:line-through}
     [data-lp-regenerating="true"]{position:relative;pointer-events:none}
     [data-lp-regenerating="true"]>.lp-studio-regenerating-copy{opacity:.5;animation:lp-studio-pulse 1.4s ease-in-out infinite}
     .lp-studio-regenerating-badge{position:absolute;left:0;top:0;z-index:20;display:inline-flex;align-items:center;
       border-radius:4px;background:rgba(17,24,39,.88);padding:2px 6px;color:white;
       font:600 10px/1.4 system-ui,sans-serif;letter-spacing:0;white-space:nowrap;pointer-events:none}
     .lp-studio-regenerating-badge:before{content:"";width:8px;height:8px;margin-right:4px;border:2px solid rgba(255,255,255,.45);
       border-top-color:white;border-radius:999px;animation:lp-studio-spin .8s linear infinite}
     @keyframes lp-studio-spin{to{transform:rotate(360deg)}}
     @keyframes lp-studio-pulse{50%{opacity:.25}}
     body{font-family:var(--lp-body-font,inherit)}
     h1,h2,h3,h4,h5,h6{font-family:var(--lp-heading-font,var(--lp-body-font,inherit))}
     ${presentation?.bridgeCss ?? ""}
    ${
      editable
        ? `[data-lp-bind]{cursor:text;outline:1px dashed transparent;outline-offset:3px}
           [data-lp-bind]:hover,[data-lp-bind]:focus{outline-color:#2563eb}
           [data-lp-bind]:focus{background:rgba(37,99,235,.06)}`
        : ""
    }
  `;
  head.append(runtimeStyle);

  const legacyProductDetailsBindings = new Map<string, string>([
    ["[PRODUCT DETAILS LABEL]", "phBuyBox.productDetails.label"],
    ["[PRODUCT DETAILS HEADING]", "phBuyBox.productDetails.heading"],
    [
      "[PRODUCT DETAILS DESCRIPTION]",
      "phBuyBox.productDetails.description",
    ],
    ...Array.from({ length: 4 }, (_, index) => index).flatMap(
      (index): Array<[string, string]> => [
        [
          `[SPEC ${index + 1} LABEL]`,
          `phBuyBox.productDetails.specs.${index}.label`,
        ],
        [
          `[SPEC ${index + 1} VALUE]`,
          `phBuyBox.productDetails.specs.${index}.value`,
        ],
      ],
    ),
  ]);
  document.querySelectorAll<HTMLElement>("body *").forEach((element) => {
    // Exact, leaf-only matching protects any customer-edited text and avoids
    // interpreting bracketed prose outside this one legacy template contract.
    if (element.hasAttribute("data-lp-bind") || element.children.length) return;
    const binding = legacyProductDetailsBindings.get(
      element.textContent?.trim() ?? "",
    );
    if (!binding) return;
    element.setAttribute("data-lp-bind", binding);
    element.setAttribute("data-lp-legacy-product-detail", "true");
  });

  document.querySelectorAll<HTMLElement>("[data-lp-bind]").forEach((element) => {
    const rawPath = element.getAttribute("data-lp-bind");
    const path = parseLpStudioBindingPath(rawPath);
    if (!path) return;
    const value =
      reviewValueForPath(runtimeContent, path) ??
      lpGetAt(runtimeContent, path);
    if (typeof value === "string" || typeof value === "number") {
      element.textContent = String(value);
    } else {
      element.textContent = "";
    }
    if (
      element.hasAttribute("data-lp-legacy-product-detail") &&
      !element.textContent.trim()
    ) {
      // Unknown product facts are omitted, never shown as fake generation
      // status or left behind as authored placeholders.
      element.hidden = true;
    }
    if (editable) {
      element.contentEditable = "true";
      element.spellcheck = true;
      element.setAttribute("data-lp-studio-editable", "true");
    }
  });

  // Template Studio intentionally authors reusable business placeholders.
  // Resolve only the two grounded literals the runtime can prove from the
  // selected records; every other bracket token remains under generated-copy
  // bindings rather than receiving invented fallback text.
  const groundedTokens = new Map<string, string>();
  if (typeof brandName === "string" && brandName.trim()) {
    groundedTokens.set("[BUSINESS NAME]", brandName.trim());
  }
  if (typeof offerText === "string" && offerText.trim()) {
    groundedTokens.set("[OFFER]", offerText.trim());
  }
  if (groundedTokens.size) {
    const walker = document.createTreeWalker(document.documentElement, 4);
    let textNode = walker.nextNode();
    while (textNode) {
      const parentTag = textNode.parentElement?.tagName;
      if (parentTag !== "STYLE" && parentTag !== "SCRIPT") {
        let value = textNode.nodeValue ?? "";
        groundedTokens.forEach((replacement, token) => {
          value = value.replaceAll(token, replacement);
        });
        textNode.nodeValue = value;
      }
      textNode = walker.nextNode();
    }
  }

  // Backward compatibility for already-frozen Studio publications created
  // before the logo binding existed. Only an exact literal [LOGO] marker is
  // upgraded; descriptive/decorative placeholders retain their authored slot.
  document
    .querySelectorAll<HTMLElement>("[data-image-placeholder], [data-lp-image]")
    .forEach((element) => {
      const labels = [
        element.getAttribute("data-image-placeholder"),
        element.getAttribute("aria-label"),
        element.getAttribute("alt"),
        element.textContent,
      ]
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.trim());
      if (
        labels.some((label) =>
          /^(?:\[LOGO\]|LOGO|BRAND-LOGO|FOOTER-LOGO)$/i.test(label),
        )
      ) {
        element.setAttribute("data-lp-image", "logo");
      }
    });

  document
    .querySelectorAll<HTMLElement>("[data-lp-image]")
    .forEach((element) => {
      const binding = element.getAttribute("data-lp-image") as LpStudioImageBinding;
      if (!["logo", "product", "lifestyle-1", "lifestyle-2"].includes(binding)) return;
      const resolved = imageUrlFor(binding, generatedContent, productPhotos, logoUrl);
      element.setAttribute("data-lp-image-status", resolved.status);
      const statusLabel =
        binding === "logo"
          ? "Brand logo unavailable"
          : binding === "product"
            ? "Product photo unavailable"
            : resolved.status === "pending"
              ? "Generating section image…"
              : resolved.status === "failed"
                ? "Section image generation failed"
                : "Section image unavailable";
      if (element instanceof HTMLImageElement) {
        if (resolved.url) {
          element.src = resolved.url;
          if (binding === "logo") {
            element.alt = `${brandName?.trim() || "Brand"} logo`;
            element.style.objectFit = "contain";
          } else if (/^\[[^\]]+\]$/.test(element.alt.trim())) {
            element.alt = "";
          }
        } else {
          element.removeAttribute("src");
          element.alt = statusLabel;
        }
      } else if (resolved.url) {
        element.style.backgroundImage = `url("${resolved.url.replaceAll('"', "%22")}")`;
        element.style.backgroundSize = binding === "logo" ? "contain" : "cover";
        element.style.backgroundPosition = "center";
        if (binding === "logo") {
          element.style.backgroundRepeat = "no-repeat";
          element.setAttribute(
            "aria-label",
            `${brandName?.trim() || "Brand"} logo`,
          );
        }
        if (/^\[[^\]]+\]$/.test(element.textContent?.trim() ?? "")) {
          element.textContent = "";
        }
      } else {
        element.setAttribute("data-lp-generation-label", "true");
        element.textContent = statusLabel;
      }
    });

  document.querySelectorAll<HTMLAnchorElement>("[data-lp-cta]").forEach((anchor) => {
    const ctaText = generatedContent.phBuyBox?.ctaText;
    if (
      typeof ctaText === "string" &&
      ctaText.trim() &&
      /^\[(?:PRIMARY )?CTA LABEL\]$/i.test(anchor.textContent?.trim() ?? "")
    ) {
      anchor.textContent = ctaText.trim();
    }
    const href = (ctaUrl ?? "").trim();
    if (href) {
      anchor.href = href;
      anchor.target = href.startsWith("#") ? "_self" : "_top";
      anchor.rel = "noopener";
    } else {
      anchor.removeAttribute("href");
    }
  });
  applyStudioElementPresentation(document, runtimeContent, editable);
  applyStudioSectionLayout(document, runtimeContent, sections, editable);

  const regeneration = generatedContent.lp2Regeneration ?? {};
  const bulkCopyPending = regeneration.copy?.status === "pending";
  const bulkImagesPending = regeneration.images?.status === "pending";
  const localSections = new Set(regeneratingSections);
  const markRegenerating = (element: HTMLElement, kind: "copy" | "image") => {
    if (element.dataset.lpRegenerating === "true") return;
    element.dataset.lpRegenerating = "true";
    element.dataset.lpRegeneratingKind = kind;
    const badge = document.createElement("span");
    badge.className = "lp-studio-regenerating-badge";
    badge.setAttribute("data-testid", `badge-regenerating-${kind}`);
    badge.textContent = "Regenerating…";
    if (kind === "copy") {
      const copy = document.createElement("span");
      copy.className = "lp-studio-regenerating-copy";
      while (element.firstChild) copy.append(element.firstChild);
      element.append(copy, badge);
    } else if (element instanceof HTMLImageElement && element.parentElement) {
      const parent = element.parentElement;
      if (getComputedStyle(parent).position === "static") {
        parent.style.position = "relative";
      }
      parent.append(badge);
    } else {
      element.append(badge);
    }
  };
  if (editable) {
    document.querySelectorAll<HTMLElement>("[data-lp-bind]").forEach((element) => {
      const root = studioSectionRoot(element.dataset.lpBind ?? null);
      if (bulkCopyPending || (root != null && localSections.has(root))) {
        markRegenerating(element, "copy");
      }
    });
    document
      .querySelectorAll<HTMLElement>(
        '[data-lp-image="lifestyle-1"],[data-lp-image="lifestyle-2"]',
      )
      .forEach((element) => {
        const binding = element.dataset.lpImage as LpStudioImageBinding;
        const index = binding === "lifestyle-1" ? 0 : 1;
        const slotKey = LP_PRODUCT_HERO_IMAGE_SLOTS[index]?.key;
        const override = slotKey
          ? generatedContent.mediaOverrides?.[`phBuyBox:gallery-ai-${slotKey}`]?.url
          : null;
        const slot = generatedContent.productHeroImages?.find(
          (candidate: any) => candidate?.key === slotKey,
        );
        if (!override && (bulkImagesPending || slot?.imageStatus === "pending")) {
          markRegenerating(element, "image");
        }
      });
  }
  const componentOrder = runtimeContent.componentOrder ?? {};
  const componentGroups = annotateStudioComponents(studioSectionContainers(document));
  componentGroups.forEach((group) => {
    const elements = group.componentKeys
      .map((key) => document.querySelector<HTMLElement>(`[data-lp-component="${key}"]`))
      .filter((element): element is HTMLElement => Boolean(element));
    reorderElements(elements, componentOrder[group.key]);
  });

  return `<!doctype html>${document.documentElement.outerHTML}`;
}

/**
 * Template Studio pages render in a script-free iframe. This preserves the
 * authored document and isolates its CSS from the app/public shell while still
 * letting the parent wire safe copy edits to the shared LP content endpoint.
 */
export default function LpExpressStudioTemplate({
  generatedContent,
  productPhotos,
  logoUrl,
  brandName,
  offerText,
  groundedProductDetails,
  ctaUrl,
  editor,
  presentation,
  sections,
  onRetryImage,
  reviews = [],
}: StudioTemplateProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(720);
  const editorRef = useRef(editor);
  const activeEditRef = useRef<{
    path: Array<string | number>;
    value: string;
    persistedValue: string;
  } | null>(null);
  const editTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editing, setEditing] = useState(false);
  const [inlineTarget, setInlineTarget] = useState<StudioInlineTarget | null>(null);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);
  const nextSrcDoc = useMemo(
    () => {
      const sectionRoots = new Set<string>();
      if (editor?.isBlockRegenerating) {
        const design = generatedContent.studioDesign;
        if (isLpStudioDesignSnapshot(design) && typeof DOMParser !== "undefined") {
          const doc = new DOMParser().parseFromString(design.html, "text/html");
          doc.querySelectorAll<HTMLElement>("[data-lp-bind]").forEach((element) => {
            const root = studioSectionRoot(element.dataset.lpBind ?? null);
            if (root && editor.isBlockRegenerating?.(root)) sectionRoots.add(root);
          });
        }
      }
      return buildStudioSrcDoc(
        generatedContent,
        productPhotos,
        ctaUrl,
        Boolean(editor),
        presentation,
        sections,
        logoUrl,
        brandName,
        offerText,
        groundedProductDetails,
        Array.from(sectionRoots),
      );
    },
    [
      generatedContent,
      productPhotos,
      ctaUrl,
      editor,
      presentation,
      sections,
      logoUrl,
      brandName,
      offerText,
      groundedProductDetails,
    ],
  );
  const [srcDoc, setSrcDoc] = useState(nextSrcDoc);

  // Do not reload the iframe underneath a focused contentEditable node. Image
  // polling and other parent hydration can change generatedContent while the
  // marketer is typing; the latest document is applied once the edit flushes.
  useEffect(() => {
    if (!editing) setSrcDoc(nextSrcDoc);
  }, [nextSrcDoc, editing]);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame || !srcDoc) return;
    const onLoad = () => {
      const document = frame.contentDocument;
      if (!document) return;
      let heightMeasurementCancelled = false;
      const heightTimers: number[] = [];
      const heightFrames: number[] = [];
      const updateHeight = () => {
        if (heightMeasurementCancelled) return;
        const nextHeight = Math.max(
            320,
            document.documentElement.scrollHeight,
            document.body?.scrollHeight ?? 0,
        );
        setHeight((currentHeight) =>
          Math.abs(currentHeight - nextHeight) > 1 ? nextHeight : currentHeight,
        );
      };
      const scheduleHeightMeasurement = (delay = 0) => {
        if (delay > 0) {
          heightTimers.push(window.setTimeout(updateHeight, delay));
          return;
        }
        const requestFrame = frame.contentWindow?.requestAnimationFrame.bind(
          frame.contentWindow,
        );
        if (requestFrame) heightFrames.push(requestFrame(updateHeight));
        else updateHeight();
      };
      // A live ResizeObserver on the iframe document creates a feedback loop:
      // updating the iframe height changes its viewport, which reflows the
      // document and schedules another resize notification. Measure only at
      // bounded lifecycle points instead.
      scheduleHeightMeasurement();
      scheduleHeightMeasurement(100);
      scheduleHeightMeasurement(500);
      const measuredImages = Array.from(document.images);
      measuredImages.forEach((image) => {
        image.addEventListener("load", updateHeight);
        image.addEventListener("error", updateHeight);
      });
      document.fonts?.ready.then(updateHeight).catch(() => undefined);
      const removeHeightMeasurements = () => {
        heightMeasurementCancelled = true;
        heightTimers.forEach((timer) => window.clearTimeout(timer));
        heightFrames.forEach((frameId) => frame.contentWindow?.cancelAnimationFrame(frameId));
        measuredImages.forEach((image) => {
          image.removeEventListener("load", updateHeight);
          image.removeEventListener("error", updateHeight);
        });
      };

      const currentEditor = editorRef.current;
      const inspectInlineTarget = (event: Event) => {
        const hovered = event.target as Element | null;
        if (!hovered?.closest) return;
        const section = hovered.closest<HTMLElement>("[data-lp-section]");
        const bound = hovered.closest<HTMLElement>("[data-lp-bind]");
        const image = hovered.closest<HTMLElement>("[data-lp-image]");
        const target = image ?? bound ?? section;
        if (!target) return;
        const sectionType = section?.dataset.lpSection;
        const imageBinding = generatedContent.studioDesign?.rendererTemplateKey === "smb-home"
          ? undefined : image?.dataset.lpImage as LpStudioImageBinding | undefined;
        setInlineTarget({
          sectionType,
          sectionTop: section?.getBoundingClientRect().top,
          binding: bound?.dataset.lpBind,
          componentKey: bound?.dataset.lpComponent,
          imageBinding,
          targetTop: target.getBoundingClientRect().top,
        });
      };
      const handleHashNavigation = (event: Event) => {
        const anchor = (event.target as Element | null)?.closest?.(
          'a[href^="#"]',
        ) as HTMLAnchorElement | null;
        if (!anchor) return;
        const id = anchor.getAttribute("href")?.slice(1);
        const target = id ? document.getElementById(id) : null;
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      document.addEventListener("click", handleHashNavigation);
      document.addEventListener("pointerover", inspectInlineTarget);
      if (!currentEditor) {
        return () => {
          removeHeightMeasurements();
          document.removeEventListener("click", handleHashNavigation);
          document.removeEventListener("pointerover", inspectInlineTarget);
        };
      }
      const persistActiveEdit = () => {
        const active = activeEditRef.current;
        if (!active || active.value === active.persistedValue) return;
        active.persistedValue = active.value;
        editorRef.current?.onEditText?.(active.path, active.value);
      };
      const preventNavigation = (event: Event) => {
        if ((event.target as Element | null)?.closest?.("a")) {
          event.preventDefault();
        }
      };
      const beginEdit = (event: FocusEvent) => {
        const element = (event.target as Element | null)?.closest?.(
          "[data-lp-studio-editable]",
        ) as HTMLElement | null;
        if (!element) return;
        const path = parseLpStudioBindingPath(element.getAttribute("data-lp-bind"));
        if (!path) return;
        const value = (element.innerText ?? element.textContent ?? "").trim();
        activeEditRef.current = { path, value, persistedValue: value };
        setEditing(true);
      };
      const updateEdit = (event: Event) => {
        const element = (event.target as Element | null)?.closest?.(
          "[data-lp-studio-editable]",
        ) as HTMLElement | null;
        if (!element || !activeEditRef.current) return;
        activeEditRef.current.value = (
          element.innerText ??
          element.textContent ??
          ""
        ).trim();
        if (editTimerRef.current) clearTimeout(editTimerRef.current);
        editTimerRef.current = setTimeout(() => {
          editTimerRef.current = null;
          persistActiveEdit();
        }, 500);
      };
      const finishEdit = () => {
        if (editTimerRef.current) {
          clearTimeout(editTimerRef.current);
          editTimerRef.current = null;
        }
        persistActiveEdit();
        activeEditRef.current = null;
        setEditing(false);
      };
      document.addEventListener("click", preventNavigation);
      document.addEventListener("focusin", beginEdit);
      document.addEventListener("input", updateEdit);
      document.addEventListener("focusout", finishEdit);
      return () => {
        removeHeightMeasurements();
        if (editTimerRef.current) {
          clearTimeout(editTimerRef.current);
          editTimerRef.current = null;
        }
        persistActiveEdit();
        document.removeEventListener("click", preventNavigation);
        document.removeEventListener("click", handleHashNavigation);
        document.removeEventListener("pointerover", inspectInlineTarget);
        document.removeEventListener("focusin", beginEdit);
        document.removeEventListener("input", updateEdit);
        document.removeEventListener("focusout", finishEdit);
      };
    };
    let removeDocumentListeners: (() => void) | undefined;
    const load = () => {
      removeDocumentListeners?.();
      removeDocumentListeners = onLoad() as (() => void) | undefined;
    };
    frame.addEventListener("load", load);
    if (frame.contentDocument?.readyState === "complete") load();
    return () => {
      frame.removeEventListener("load", load);
      removeDocumentListeners?.();
      activeEditRef.current = null;
    };
  }, [srcDoc]);

  const authoredSectionTypes = useMemo(() => {
    const design = generatedContent.studioDesign;
    if (!isLpStudioDesignSnapshot(design) || typeof DOMParser === "undefined") {
      return new Set<string>();
    }
    const document = new DOMParser().parseFromString(design.html, "text/html");
    return new Set(
      Array.from(document.querySelectorAll("[data-lp-bind]"))
        .map((element) => studioSectionRoot(element.getAttribute("data-lp-bind")))
        .filter((value): value is string => Boolean(value)),
    );
  }, [generatedContent.studioDesign]);
  const authoredBindings = useMemo(() => {
    const design = generatedContent.studioDesign;
    if (!isLpStudioDesignSnapshot(design) || typeof DOMParser === "undefined") {
      return [] as string[];
    }
    const document = new DOMParser().parseFromString(design.html, "text/html");
    return Array.from(document.querySelectorAll("[data-lp-bind]"))
      .map((element) => element.getAttribute("data-lp-bind")?.trim() ?? "")
      .filter((value, index, all) => Boolean(parseLpStudioBindingPath(value)) && all.indexOf(value) === index);
  }, [generatedContent.studioDesign]);
  const componentGroups = useMemo(() => {
    const design = generatedContent.studioDesign;
    if (!isLpStudioDesignSnapshot(design) || typeof DOMParser === "undefined") {
      return [] as StudioComponentGroup[];
    }
    const document = new DOMParser().parseFromString(design.html, "text/html");
    return annotateStudioComponents(studioSectionContainers(document));
  }, [generatedContent.studioDesign]);
  const authoredItems = useMemo(() => {
    const byKey = new Map<
      string,
      { key: string; path: Array<string | number>; index: number; count: number; hidden: boolean; canHide: boolean }
    >();
    for (const binding of authoredBindings) {
      const path = parseLpStudioBindingPath(binding);
      if (!path) continue;
      const indexPosition = path.findIndex((part) => typeof part === "number");
      if (indexPosition < 0) continue;
      const index = path[indexPosition] as number;
      const arrayPath = path.slice(0, indexPosition);
      const items = lpGetAt(generatedContent, arrayPath);
      if (!Array.isArray(items) || !items[index]) continue;
      const key = `${arrayPath.join(".")}:${index}`;
      byKey.set(key, {
        key,
        path: arrayPath,
        index,
        count: items.length,
        hidden: Boolean(items[index]?.hidden),
        canHide: typeof items[index] === "object" && items[index] !== null,
      });
    }
    return Array.from(byKey.values());
  }, [authoredBindings, generatedContent]);

  if (!srcDoc) {
    return (
      <div className="p-8 text-center text-sm text-red-700" role="alert">
        This Studio template is missing its frozen design. Generate a new page
        from a valid publication.
      </div>
    );
  }

  const authoredSections = (sections ?? []).filter((section) =>
    authoredSectionTypes.has(section.type),
  );
  const activeSection = inlineTarget?.sectionType
    ? authoredSections.find((section) => section.type === inlineTarget.sectionType)
    : undefined;
  const activeSectionIndex = activeSection
    ? authoredSections.findIndex((section) => section.type === activeSection.type)
    : -1;
  const activeBinding = inlineTarget?.binding;
  const activeElementKey = activeBinding ? `f:${activeBinding}` : undefined;
  const activeElementState = activeElementKey
    ? generatedContent.elements?.[activeElementKey]
    : undefined;
  const activeItemIdentity = studioItemIdentity(activeBinding ?? null);
  const activeItem = activeItemIdentity
    ? authoredItems.find((item) => item.key === activeItemIdentity.key)
    : undefined;
  const activeComponentGroup = inlineTarget?.componentKey
    ? componentGroups.find((group) =>
        group.componentKeys.includes(inlineTarget.componentKey!),
      )
    : undefined;
  const activeComponentOrder = activeComponentGroup
    ? (generatedContent.componentOrder?.[activeComponentGroup.key] ??
      activeComponentGroup.componentKeys)
    : [];
  const activeComponentIndex = inlineTarget?.componentKey
    ? activeComponentOrder.indexOf(inlineTarget.componentKey)
    : -1;
  const activeImageBinding = inlineTarget?.imageBinding;
  const activeImageIndex =
    activeImageBinding === "lifestyle-1"
      ? 0
      : activeImageBinding === "lifestyle-2"
        ? 1
        : null;
  const activeImageSlot =
    activeImageIndex == null ? null : LP_PRODUCT_HERO_IMAGE_SLOTS[activeImageIndex];
  const activeImageSlotKey =
    activeImageBinding === "product"
      ? "phBuyBox:gallery-main"
      : activeImageBinding
        ? `phBuyBox:gallery-ai-${activeImageSlot?.key ?? activeImageBinding}`
        : "";
  const activeReviewIndex =
    activeBinding?.startsWith("phReviews.items.") &&
    typeof parseLpStudioBindingPath(activeBinding)?.[2] === "number"
      ? (parseLpStudioBindingPath(activeBinding)?.[2] as number)
      : null;

  return (
    <div className="relative" data-testid="lp-studio-editor-canvas">
      <iframe
        ref={iframeRef}
        title="Landing page template"
        srcDoc={srcDoc}
        sandbox="allow-same-origin allow-top-navigation-by-user-activation allow-forms"
        className="block w-full border-0 bg-white"
        style={{ height }}
        data-testid="lp-express-studio-template"
      />
      {editor && activeSection && (
        <div
          className="absolute left-2 z-20 flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1 text-xs shadow-md"
          style={{ top: Math.max(4, inlineTarget?.sectionTop ?? 4) }}
          data-testid={`lp-studio-inline-section-${activeSection.type}`}
        >
          <span>{LP_SECTION_REGISTRY[activeSection.type]?.label ?? activeSection.type}</span>
          <button
            type="button"
            disabled={editor.busy || activeSectionIndex === 0}
            onClick={() => editor.onMove(activeSection.type, "up")}
            aria-label={`Move ${activeSection.type} up`}
          >
            ↑
          </button>
          <button
            type="button"
            disabled={editor.busy || activeSectionIndex === authoredSections.length - 1}
            onClick={() => editor.onMove(activeSection.type, "down")}
            aria-label={`Move ${activeSection.type} down`}
          >
            ↓
          </button>
          <button type="button" disabled={editor.busy} onClick={() => editor.onToggleHidden(activeSection.type)}>
            {activeSection.hidden ? "Show" : "Hide"}
          </button>
          <button type="button" disabled={editor.busy} onClick={() => editor.onDelete(activeSection.type)}>
            Delete
          </button>
          <LpSectionRegenerate type={activeSection.type} />
          <LpStylePopover elementKey={`sec:${activeSection.type}`} variant="section" />
        </div>
      )}
      {editor && (activeBinding || activeImageBinding) && (
        <div
          className="group/lpfield group/lpimg group/lpreview absolute right-2 z-30 flex items-center gap-1 rounded-md border border-primary/30 bg-white px-2 py-1 text-xs shadow-md"
          style={{ top: Math.max(42, inlineTarget?.targetTop ?? 42) }}
          data-testid="lp-studio-inline-element-tools"
        >
          {activeImageBinding ? (
            <>
              <span>{activeImageBinding === "product" ? "Product image" : `Lifestyle image ${activeImageIndex! + 1}`}</span>
              <LpImageActions
                slotKey={activeImageSlotKey}
                canRegenerate={activeImageIndex != null && Boolean(onRetryImage)}
                onRegenerate={
                  activeImageIndex != null && onRetryImage
                    ? () => onRetryImage(activeImageSlot!.key)
                    : undefined
                }
                productPhotos={productPhotos}
                revertLabel="Use default"
                revertTitle="Use the default page image"
                className="!static !opacity-100"
                testIdPrefix={`studio-${activeImageBinding}`}
              />
            </>
          ) : activeBinding && activeElementKey ? (
            <>
              <LpStylePopover elementKey={activeElementKey} variant="text" />
              {editor.onSetFieldHidden && (
                <button
                  type="button"
                  disabled={editor.contentBusy}
                  onClick={() => editor.onSetFieldHidden?.(activeElementKey, !activeElementState?.hidden)}
                  data-testid={`button-studio-field-visibility-${activeBinding}`}
                >
                  {activeElementState?.hidden ? "Show" : "Hide"}
                </button>
              )}
              {editor.onDeleteField && (
                <button
                  type="button"
                  disabled={editor.contentBusy}
                  onClick={() => editor.onDeleteField?.(activeElementKey)}
                  data-testid={`button-studio-field-delete-${activeBinding}`}
                >
                  Delete
                </button>
              )}
              {activeItem && (
                <>
                  <button
                    type="button"
                    disabled={editor.contentBusy || activeItem.index === 0}
                    onClick={() => editor.onItemMove?.(activeItem.path, activeItem.index, "up")}
                    data-testid={`button-studio-item-up-${activeItem.key}`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    disabled={editor.contentBusy || activeItem.index === activeItem.count - 1}
                    onClick={() => editor.onItemMove?.(activeItem.path, activeItem.index, "down")}
                    data-testid={`button-studio-item-down-${activeItem.key}`}
                  >
                    ↓
                  </button>
                  {activeItem.canHide && (
                    <button
                      type="button"
                      disabled={editor.contentBusy}
                      onClick={() => editor.onItemSetHidden?.(activeItem.path, activeItem.index, !activeItem.hidden)}
                      data-testid={`button-studio-item-visibility-${activeItem.key}`}
                    >
                      {activeItem.hidden ? "Show item" : "Hide item"}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={editor.contentBusy}
                    onClick={() => editor.onItemDelete?.(activeItem.path, activeItem.index)}
                    data-testid={`button-studio-item-delete-${activeItem.key}`}
                  >
                    Delete item
                  </button>
                </>
              )}
              {activeComponentGroup && inlineTarget?.componentKey && (
                <>
                  <button
                    type="button"
                    disabled={editor.contentBusy || activeComponentIndex <= 0}
                    onClick={() => {
                      const next = [...activeComponentOrder];
                      [next[activeComponentIndex - 1], next[activeComponentIndex]] =
                        [next[activeComponentIndex], next[activeComponentIndex - 1]];
                      editor.onMoveElement?.(activeComponentGroup.key, next);
                    }}
                    data-testid={`button-studio-component-up-${inlineTarget.componentKey}`}
                  >
                    Block ↑
                  </button>
                  <button
                    type="button"
                    disabled={editor.contentBusy || activeComponentIndex < 0 || activeComponentIndex === activeComponentOrder.length - 1}
                    onClick={() => {
                      const next = [...activeComponentOrder];
                      [next[activeComponentIndex], next[activeComponentIndex + 1]] =
                        [next[activeComponentIndex + 1], next[activeComponentIndex]];
                      editor.onMoveElement?.(activeComponentGroup.key, next);
                    }}
                    data-testid={`button-studio-component-down-${inlineTarget.componentKey}`}
                  >
                    Block ↓
                  </button>
                </>
              )}
              {activeReviewIndex != null && reviews.length > 0 && (
                <LpReviewActions
                  slotKey={`studio-review-${activeReviewIndex}`}
                  reviews={reviews}
                  className="!static !opacity-100"
                  testIdPrefix={`studio-review-${activeReviewIndex}`}
                />
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}